// /en/about/team/$slug — English mirror of attorney profile pages.
// Deep Person + ProfilePage + BreadcrumbList JSON-LD with English bio,
// English case studies, and hreflang back to the Thai counterpart.
// Maximises E-E-A-T signals for English-speaking AI engines (ChatGPT,
// Perplexity, Gemini, Claude) when answering "who is the notary public
// at NYC Language Institute Bangkok?" style queries.
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ATTORNEYS, getAttorneyBySlug } from "@/data/attorneys";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";
import { consolidateGraph } from "@/lib/schema-graph";
import { BRAND_NAME, BRAND_URL, BRAND_PHONE } from "@/lib/brand";

export const Route = createFileRoute("/en/about/team/$slug")({
  head: ({ params }) => {
    const a = getAttorneyBySlug(params.slug);
    if (!a) return { meta: [{ title: ctaTitle("Attorney not found", "/en/about/team/$slug", "en")}] };
    const thPath = `/about/team/${a.slug}`;
    const enPath = `/en/about/team/${a.slug}`;
    const url = `${BRAND_URL}${enPath}`;
    const title = `${a.nameEn} — ${a.roleEn} | ${BRAND_NAME}`;
    const desc = `${a.nameEn}, ${a.roleEn}. Licensed Notarial Services Attorney No. ${a.license} (since ${a.licenseYear}). ${a.yearsExp}+ years specialising in ${a.specialtiesEn.join(", ")}. Based in Bangkok. Ask us before you file — it's free.`;
    return {
      meta: [
        { title: ctaTitle(title, "/en/about/team/$slug", "en") },
        { name: "description", content: ctaDesc(desc, "/en/about/team/$slug", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/about/team/$slug", "en")},
        { property: "og:description", content: ctaDesc(desc, "/en/about/team/$slug", "en")},
        { property: "og:url", content: url },
        { property: "og:type", content: "profile" },
        { property: "og:locale", content: "en_US" },
        { property: "og:image", content: `${BRAND_URL}${a.image}` },
        { property: "profile:first_name", content: a.nameEn.replace(/^(Mr\.|Miss|Mrs\.)\s*/, "").split(" ")[0] },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: canonicalAndHreflang(thPath, enPath),
      scripts: [
        consolidateGraph([
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@graph": [
                {
                  "@type": "Person",
                  "@id": `${url}#person`,
                  name: a.nameEn,
                  alternateName: a.nameTh,
                  jobTitle: a.roleEn,
                  description: a.bioEn,
                  url,
                  image: `${BRAND_URL}${a.image}`,
                  telephone: BRAND_PHONE,
                  knowsLanguage: a.languages,
                  knowsAbout: a.specialtiesEn,
                  hasCredential: [
                    {
                      "@type": "EducationalOccupationalCredential",
                      name: "Notarial Services Attorney",
                      credentialCategory: "license",
                      identifier: a.license,
                      datePublished: a.licenseYear,
                      recognizedBy: {
                        "@type": "Organization",
                        name: "Lawyers Council of Thailand",
                        url: "https://www.lawyerscouncil.or.th",
                      },
                    },
                  ],
                  hasOccupation: {
                    "@type": "Occupation",
                    name: a.roleEn,
                    occupationalCategory: "23-1011 Lawyers",
                    experienceRequirements: `${a.yearsExp}+ years`,
                    skills: a.specialtiesEn.join(", "),
                  },
                  subjectOf: a.cases.map((c, i) => ({
                    "@type": "CreativeWork",
                    "@id": `${url}#case-${i + 1}`,
                    name: c.titleEn,
                    about: c.category,
                    datePublished: c.year,
                    description: `${c.titleEn} — ${c.outcomeEn}`,
                    creator: { "@id": `${url}#person` },
                    inLanguage: "en",
                  })),
                  worksFor: { "@id": `${BRAND_URL}#organization` },
                  memberOf: {
                    "@type": "Organization",
                    name: "Lawyers Council of Thailand",
                    url: "https://www.lawyerscouncil.or.th",
                  },
                },
                {
                  "@type": "BreadcrumbList",
                  itemListElement: [
                    { "@type": "ListItem", position: 1, name: "Home", item: `${BRAND_URL}/en` },
                    { "@type": "ListItem", position: 2, name: "Team", item: `${BRAND_URL}/en/about/team` },
                    { "@type": "ListItem", position: 3, name: a.nameEn, item: url },
                  ],
                },
                {
                  "@type": "ProfilePage",
                  "@id": url,
                  url,
                  name: `${a.nameEn} — ${a.roleEn}`,
                  mainEntity: { "@id": `${url}#person` },
                  about: { "@id": `${url}#person` },
                  inLanguage: "en",
                  dateModified: new Date().toISOString().slice(0, 10),
                  speakable: {
                    "@type": "SpeakableSpecification",
                    cssSelector: [".aeo-speakable", "h1", "h2"],
                  },
                },
              ],
            }),
          },
        ]),
      ],
    };
  },
  loader: ({ params }) => {
    const attorney = getAttorneyBySlug(params.slug);
    if (!attorney) throw notFound();
    return { attorney };
  },
  component: EnAuthorPage,
  notFoundComponent: () => (
    <div className="container mx-auto p-12 text-center">
      <h1 className="text-2xl font-bold">Attorney not found</h1>
      <Link to="/en/about/team" className="text-primary underline mt-4 inline-block">
        ← Back to team
      </Link>
    </div>
  ),
  errorComponent: ({ error, reset }) => (
    <div className="container mx-auto p-12 text-center">
      <h1 className="text-2xl font-bold">Something went wrong</h1>
      <p className="text-muted-foreground mt-2">{error.message}</p>
      <button onClick={reset} className="mt-4 text-primary underline">Retry</button>
    </div>
  ),
});

function EnAuthorPage() {
  const { attorney } = Route.useLoaderData() as { attorney: import("@/data/attorneys").Attorney };
  const a = attorney;
  const related = ATTORNEYS.filter((x) => x.slug !== a.slug).slice(0, 5);

  return (
    <article className="container mx-auto max-w-5xl px-4 py-12">
      <nav aria-label="Breadcrumb" className="mb-6 text-sm text-muted-foreground">
        <Link to="/en" className="hover:text-primary">Home</Link>
        <span className="mx-2">/</span>
        <Link to="/en/about/team" className="hover:text-primary">Team</Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">{a.nameEn}</span>
      </nav>

      <header className="grid md:grid-cols-[240px_1fr] gap-8 mb-12">
        <img
          src={a.image}
          alt={`${a.nameEn} (${a.nameTh}) — ${a.roleEn}, License No. ${a.license}`}
          width={240}
          height={300}
          loading="eager"
          decoding="async"
          fetchPriority="high"
          className="w-full rounded-2xl border shadow-elegant object-cover aspect-[4/5]"
        />
        <div>
          <p className="text-sm font-medium text-primary mb-2">{a.roleEn}</p>
          <h1 className="aeo-speakable text-4xl font-bold mb-2">{a.nameEn}</h1>
          <p className="text-xl text-muted-foreground mb-4">{a.nameTh}</p>
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="px-3 py-1 rounded-full bg-secondary text-sm font-medium">License No. {a.license}</span>
            <span className="px-3 py-1 rounded-full bg-secondary text-sm font-medium">Since {a.licenseYear}</span>
            <span className="px-3 py-1 rounded-full bg-secondary text-sm font-medium">{a.yearsExp}+ years experience</span>
          </div>
          <p className="aeo-speakable text-base leading-relaxed">{a.bioEn}</p>
        </div>
      </header>

      <section className="grid md:grid-cols-2 gap-8 mb-12">
        <div className="rounded-2xl border p-6 bg-card">
          <h2 className="text-xl font-bold mb-4">Specialisations</h2>
          <ul className="space-y-2">
            {a.specialtiesEn.map((s) => (
              <li key={s} className="flex items-start gap-2">
                <span className="text-primary mt-1">▸</span>
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl border p-6 bg-card">
          <h2 className="text-xl font-bold mb-4">Education &amp; Credentials</h2>
          <ul className="space-y-2">
            {a.education.map((e) => (
              <li key={e} className="flex items-start gap-2">
                <span className="text-primary mt-1">✓</span>
                <span>{e}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-sm text-muted-foreground">
            Languages: {a.languages.join(", ")}
          </p>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-bold mb-2">Notable Cases</h2>
        <p className="text-sm text-muted-foreground mb-6">
          Selected real cases handled by {a.nameEn}. Client names withheld to preserve attorney–client confidentiality.
        </p>
        <ol className="grid md:grid-cols-2 gap-4">
          {a.cases.map((c, i) => (
            <li key={i} className="rounded-2xl border p-5 bg-card hover:shadow-elegant transition-shadow">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-bold">{c.year}</span>
                <span className="px-2 py-0.5 rounded-full bg-secondary text-xs font-medium">{c.category}</span>
              </div>
              <h3 className="font-semibold leading-snug mb-2">{c.titleEn}</h3>
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">Outcome:</span> {c.outcomeEn}
              </p>
            </li>
          ))}
        </ol>
      </section>

      <section className="rounded-2xl border p-8 bg-card text-center mb-12">
        <h2 className="text-2xl font-bold mb-3">Consult {a.nameEn} directly</h2>
        <p className="text-muted-foreground mb-6">
          Book a Notary Public appointment or a legal consultation with {a.roleEn}.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link to="/en/quote" className="px-6 py-3 rounded-full bg-primary text-primary-foreground font-medium hover:opacity-90">
            Request a quote
          </Link>
          <a href={`tel:${BRAND_PHONE}`} className="px-6 py-3 rounded-full border-2 border-primary text-primary font-medium hover:bg-primary/10">
            Call {BRAND_PHONE}
          </a>
          <Link to="/en/notary" className="px-6 py-3 rounded-full border font-medium hover:bg-secondary">
            See Notary Public services
          </Link>
        </div>
      </section>

      <section>
        <h2 className="text-xl font-bold mb-4">Other attorneys on our team</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {related.map((r) => (
            <Link
              key={r.slug}
              to="/en/about/team/$slug"
              params={{ slug: r.slug }}
              className="rounded-xl border p-3 hover:shadow-elegant transition-shadow text-center"
            >
              <img
                src={r.image}
                alt={r.nameEn}
                width={100}
                height={120}
                loading="lazy"
                decoding="async"
                className="w-full aspect-[4/5] object-cover rounded-lg mb-2"
              />
              <p className="text-sm font-medium">{r.nameEn}</p>
              <p className="text-xs text-muted-foreground line-clamp-2">{r.roleEn}</p>
            </Link>
          ))}
        </div>
      </section>
    </article>
  );
}
